import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);
const LOCAL_KEY = "hk_cart_v1";

function readLocal() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"); } catch { return []; }
}
function writeLocal(items) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(items)); } catch { /* noop */ }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const savingRef = useRef(false);
  const skipSaveRef = useRef(false); // ilk yüklemede otomatik saved etme
  const loadedUserId = useRef(null);

  const isCompanyUser = user && user.role === "company_user";

  const persistToServer = useCallback(async (nextItems) => {
    if (!isCompanyUser) return;
    try {
      savingRef.current = true;
      const payload = nextItems.map(i => ({ product_id: i.product_id, qty: i.qty }));
      await api.put("/cart", { items: payload });
    } catch (_e) {
      // sessizce geç — bir sonraki değişimde tekrar dener
    } finally {
      savingRef.current = false;
    }
  }, [isCompanyUser]);

  // Kullanıcı değiştiğinde sepeti yükle (login/logout/farklı user)
  useEffect(() => {
    if (user === null) return; // hâlâ yükleniyor
    (async () => {
      if (isCompanyUser) {
        try {
          // önce localStorage'daki misafir sepetini sunucuya birleştir (bir kereye mahsus)
          const localItems = readLocal();
          if (localItems.length > 0) {
            try {
              const payload = localItems.map(i => ({ product_id: i.product_id, qty: i.qty }));
              await api.post("/cart/merge", { items: payload });
              writeLocal([]); // temizle
            } catch { /* ignore */ }
          }
          const { data } = await api.get("/cart");
          skipSaveRef.current = true;
          setItems(data.items || []);
          loadedUserId.current = user.id;
        } catch {
          skipSaveRef.current = true;
          setItems([]);
        } finally {
          setLoaded(true);
        }
      } else {
        // misafir / admin / stock → localStorage kullan
        skipSaveRef.current = true;
        setItems(readLocal());
        loadedUserId.current = null;
        setLoaded(true);
      }
    })();
  }, [user, isCompanyUser]);

  // Değişiklikleri persist et
  useEffect(() => {
    if (!loaded) return;
    if (skipSaveRef.current) { skipSaveRef.current = false; return; }
    if (isCompanyUser) {
      persistToServer(items);
    } else {
      writeLocal(items);
    }
  }, [items, loaded, isCompanyUser, persistToServer]);

  const add = (product, qty = 1) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.product_id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        list_price: product.list_price,
        your_price: product.your_price ?? product.list_price,
        image: (product.images || [])[0] || null,
        qty,
      }];
    });
    toast.success("Sepete eklendi", { description: product.name });
  };

  const updateQty = (product_id, qty) => {
    setItems(prev => prev.map(x => x.product_id === product_id ? { ...x, qty: Math.max(1, qty) } : x));
  };

  const remove = (product_id) => {
    setItems(prev => prev.filter(x => x.product_id !== product_id));
  };

  const clear = () => {
    setItems([]);
    if (isCompanyUser) {
      api.delete("/cart").catch(() => {});
    }
  };

  const total = items.reduce((s, x) => s + ((x.your_price ?? x.list_price ?? 0) * x.qty), 0);
  const count = items.reduce((s, x) => s + x.qty, 0);

  return (
    <CartContext.Provider value={{ items, add, updateQty, remove, clear, total, count, loaded }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
